import { HubPageFragment } from "@/__generated__/graphql";
import { mediaQueries } from "@/theme/lib";
import { ComponentProps } from "@components/ComponentProps";
import Hero, { CreateHeroProps } from "@components/Hero";
import PageMeta from "@components/PageMeta";
import PreviewEditLink from "@components/PreviewEditLink";
import Slices from "@components/slices";
import { GlobalPageProps, isBicentenary } from "@lib/utils";
import dayjs from "dayjs";
import { ReactElement } from "react";
import { JsonLd } from "react-schemaorg";
import { Article } from "schema-dts";
import styled, { css } from "styled-components";

export type HubPageProps = ComponentProps<
  HubPageFragment
>;

const StyledMain = styled.main`
  ${(props) => isBicentenary(props.theme.key)  ? css`
    & > .background-enabled:has(+ .background-enabled) {
      margin-bottom: 0;
    }
    & > .CarouselParagraph {
      > div {
        margin-bottom: 0;
        ${mediaQueries.md} {
          margin-bottom: 0;
        }
      }
      ${mediaQueries.md} {
        margin-bottom: 4rem;
      }
    }
  `: ``}
`;

export default function HubPage(props: HubPageProps & GlobalPageProps): ReactElement {
  const { id, previewEditUrl, latestVersion, versionStatus, published, path, metatags, breadcrumbs, title, content, lastRevised, created, hero, pageUrl, siteName, homepageUrl } = props

  const newHero = hero ? { ...hero } : null;
  const heroProps = newHero ? CreateHeroProps(newHero, 'bicentenary') : null;

  return (
    <>
      <PageMeta
        metatags={metatags}
        breadcrumbs={breadcrumbs}
        path={path}
      />
      <JsonLd<Article>
        item={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": heroProps?.title || title,
          "description": metatags?.metaDescription || '',
          "image": metatags?.metaImage?.md,
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": pageUrl,
          },
          "author": {
            "@type": "Organization",
            "name": siteName,
            "url": homepageUrl,
          },
          "publisher": {
            "@type": "Organization",
            "name": siteName,
            "url": homepageUrl,
          },
          "dateModified": lastRevised ? dayjs.unix(parseInt(lastRevised)).format('YYYY-MM-DD') : undefined,
          "datePublished": created ? dayjs.unix(parseInt(created)).format('YYYY-MM-DD') : undefined,
        }}
      />
      {previewEditUrl &&
        <PreviewEditLink url={previewEditUrl} latestVersion={latestVersion} versionStatus={versionStatus} published={published} pageUrl={pageUrl}/>
      }

      {heroProps && <Hero {...heroProps} />}
      <StyledMain>
        {content && <Slices {...content} />}
      </StyledMain>
    </>
  );
}
